import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';

import { Observable } from 'rxjs';

import { environment } from '../../../environments/environment';

import { QueryOptions } from './QueryTypes/QueryOptions';

@Injectable()
export class UrlService {

  constructor(
    private http: HttpClient
  ) { }

  public createGetUrl(appName: string, controller: string, action: string, id: string, options: QueryOptions): string {
    var query: string = '';

    if (options) {
      //Query Parameters
      if (options.queryParameters) {
        for (let parm of options.queryParameters) {
          query += `${(query ? '&' : '')}${parm.field}=${parm.value}`;
        }
      }

      if (options.odataQuery) {
        //Filter
        if (options.odataQuery.filter) {
          var filterString: string = options.odataQuery.filter.toFilterString();

          if (filterString) {
            query += `${(query ? '&' : '')}$filter=${filterString}`;
          }
        }

        //Sort
        if (options.odataQuery.sort) {
          var sortString: string = '';

          for (let sort of options.odataQuery.sort) {
            sortString += `${(sortString ? ',' : '')}${(sort.toSortString())}`;
          }

          if (sortString) {
            query += `${(query ? '&' : '')}$orderby=${sortString}`;
          }
        }

        //Select
        if (options.odataQuery.select) {
          var selectString: string = '';

          for (let select of options.odataQuery.select) {
            selectString += `${(selectString ? ',' : '')}${(select.toSelectString())}`;
          }

          if (selectString) {
            query += `${(query ? '&' : '')}$select=${selectString}`;
          }
        }

        //Paging
        if (options.odataQuery.pageSize && options.odataQuery.page) {
          var skip: number = Math.round(options.odataQuery.pageSize) * (Math.round(options.odataQuery.page) - 1);

          if (skip < 0)
            skip = 0;

          var take: number = Math.round(options.odataQuery.pageSize);

          query += `${(query ? '&' : '')}$top=${take}&$skip=${skip}`;
        }

        //Count
        if (options.odataQuery.isCount) {
          query += `${(query ? '&' : '')}$count=true`;
        }
      }
    }

    return `${environment.apiSource}${appName}/${controller}${action ? '/' + action : ''}${id ? '/' + id : ''}${(query ? '?' : '')}${query}`;
  }

  public getBlob(appName: string, controller: string, action: string, id: string, options: QueryOptions): Observable<Blob> {
    var url = this.createGetUrl(appName, controller, action, id, options);

    return this.http.get(url, {
      observe: 'body',
      responseType: 'blob'
    });
  }

  public postBlob(appName: string, controller: string, action: string, payload: any, options: QueryOptions): Observable<Blob> {
    var query: string = '';
    if (options) {
      //Query Parameters
      if (options.queryParameters) {
        for (let parm of options.queryParameters) {
          query += `${(query ? '&' : '')}${parm.field}=${parm.value}`;
        }
      }
    }

    var url = `${environment.apiSource}${appName}/${controller}${action ? '/' + action : ''}${query}`;

    return this.http.post(url, payload, {
      observe: 'body',
      responseType: 'blob'
    });
  }

  public download(appName: string, controller: string, action: string, id: string, options: QueryOptions): void {
    var url = this.createGetUrl(appName, controller, action, id, options);

    if (options && options.useAchor) {
      const anchor = document.createElement("a");
      anchor.href = url;
      anchor.download = options.fileName ? options.fileName : 'file.txt';
      document.body.appendChild(anchor);
      anchor.click();
      document.body.removeChild(anchor);
    } else {
      window.open(url);
    }
  }

  public get<T>(appName: string, controller: string, action: string, id: string, options: QueryOptions): Observable<T> {
    var url = this.createGetUrl(appName, controller, action, id, options);

    return this.http.get<T>(url);
  }

  public post<T>(appName: string, controller: string, action: string, payload: any, options: QueryOptions) {
    var query: string = '';
    var httpOptions: any = {};
    var httpheaderOptions: HttpHeaders;
    if (options) {
      //Query Parameters
      if (options.queryParameters) {
        for (let parm of options.queryParameters) {
          query += `${(query ? '&' : '')}${parm.field}=${parm.value}`;
        }
      }
    }
    return this.http.post<T>(`${environment.apiSource}${appName}/${controller}${action ? '/' + action : ''}${query ? '?' : ''}${query}`, payload);
  }

  public postFile<T>(appName: string, controller: string, action: string, payload: any, options: QueryOptions) {
    var query: string = '';
    var httpOptions: any = {};
    var httpheaderOptions: HttpHeaders;
    if (options) {
      //Query Parameters
      if (options.queryParameters) {
        for (let parm of options.queryParameters) {
          query += `${(query ? '&' : '')}${parm.field}=${parm.value}`;
        }
      }
      if (options.reportProgress) {
        httpheaderOptions = new HttpHeaders({
          'Content-Type': 'multipart/form-data', 'boundary': 'something'
        });
        httpOptions = { reportProgress: true, observe: 'events' };
      }
    }

    return this.http.post<T>(`${environment.apiSource}${appName}/${controller}${action ? '/' + action : ''}${query}`, payload, httpOptions);
  }

  public put<T>(appName: string, controller: string, action: string, id: string, payload: any, options: QueryOptions) {
    var query: string = '';

    if (options) {
      //Query Parameters
      if (options.queryParameters) {
        for (let parm of options.queryParameters) {
          query += `${(query ? '&' : '')}${parm.field}=${parm.value}`;
        }
      }
    }

    return this.http.put<T>(`${environment.apiSource}${appName}/${controller}${action ? '/' + action : ''}${id ? '/' + id : ''}${query}`, payload);
  }

  public delete<T>(appName: string, controller: string, action: string, id: string, options: QueryOptions) {
    var query: string = '';

    if (options) {
      //Query Parameters
      if (options.queryParameters) {
        for (let parm of options.queryParameters) {
          query += `${(query ? '&' : '')}${parm.field}=${parm.value}`;
        }
      }
    }

    return this.http.delete<T>(`${environment.apiSource}${appName}/${controller}${action ? '/' + action : ''}${id ? '/' + id : ''}${query}`);
  }

  public deleteWithPayload<T>(appName: string, controller: string, action: string, id: string, payload: any, options: QueryOptions) {
    var query: string = '';

    if (options) {
      //Query Parameters
      if (options.queryParameters) {
        for (let parm of options.queryParameters) {
          query += `${(query ? '&' : '')}${parm.field}=${parm.value}`;
        }
      }
    }

    const httpOptions = {
      body: payload
    }

    return this.http.delete<T>(`${environment.apiSource}${appName}/${controller}${action ? '/' + action : ''}${id ? '/' + id : ''}${query}`, httpOptions);
  }
}

///////////////////////////////////////
////////////////Exports////////////////
///////////////////////////////////////

/////////////////OdataTypes/////////////////
export * from './OdataTypes/OdataQuery';

//Fields
export * from './OdataTypes/Field/IOdataField';
export * from './OdataTypes/Field/OdataFieldName';
export * from './OdataTypes/Field/OdataFieldNumber';
export * from './OdataTypes/Field/OdataFieldString';
export * from './OdataTypes/Field/OdataFieldDate';
export * from './OdataTypes/Field/OdataFieldDateOnly';
export * from './OdataTypes/Field/OdataFieldBoolean';
////Operators
//////Basic Operators
export * from './OdataTypes/Field/Operators/BasicOperators/OdataFieldBasicOperator';
export * from './OdataTypes/Field/Operators/BasicOperators/OdataFieldOperatorCeiling';
export * from './OdataTypes/Field/Operators/BasicOperators/OdataFieldOperatorDay';
export * from './OdataTypes/Field/Operators/BasicOperators/OdataFieldOperatorFloor';
export * from './OdataTypes/Field/Operators/BasicOperators/OdataFieldOperatorHour';
export * from './OdataTypes/Field/Operators/BasicOperators/OdataFieldOperatorLength';
export * from './OdataTypes/Field/Operators/BasicOperators/OdataFieldOperatorMinute';
export * from './OdataTypes/Field/Operators/BasicOperators/OdataFieldOperatorMonth';
export * from './OdataTypes/Field/Operators/BasicOperators/OdataFieldOperatorRound';
export * from './OdataTypes/Field/Operators/BasicOperators/OdataFieldOperatorSecond';
export * from './OdataTypes/Field/Operators/BasicOperators/OdataFieldOperatorToLower';
export * from './OdataTypes/Field/Operators/BasicOperators/OdataFieldOperatorToUpper';
export * from './OdataTypes/Field/Operators/BasicOperators/OdataFieldOperatorTrim';
export * from './OdataTypes/Field/Operators/BasicOperators/OdataFieldOperatorYear';
export * from './OdataTypes/Field/Operators/BasicOperators/OdataFieldOperatorDate';
//////TwoValueOperators
export * from './OdataTypes/Field/Operators/TwoValueOperators/OdataFieldTwoValueOperator';
export * from './OdataTypes/Field/Operators/TwoValueOperators/OdataFieldOperatorConcat';
export * from './OdataTypes/Field/Operators/TwoValueOperators/OdataFieldOperatorIndexOf';
export * from './OdataTypes/Field/Operators/TwoValueOperators/OdataFieldOperatorSubstring';
//////ThreeValueOperators
export * from './OdataTypes/Field/Operators/ThreeValueOperators/OdataFieldThreeValueOperator';
export * from './OdataTypes/Field/Operators/ThreeValueOperators/OdataFieldOperatorReplace';

//Filters
export * from './OdataTypes/Filter/IOdataFilter';
export * from './OdataTypes/Filter/OdataFilterCompare';
export * from './OdataTypes/Filter/OdataFilterEndsWith';
export * from './OdataTypes/Filter/OdataFilterGroup';
export * from './OdataTypes/Filter/OdataFilterStartsWith';
export * from './OdataTypes/Filter/OdataFilterSubstringOf';

//Selects
export * from './OdataTypes/Select/OdataSelect';

//Sorts
export * from './OdataTypes/Sort/OdataSort';


/////////////////QueryTypes/////////////////
export * from './QueryTypes/QueryOptions';
export * from './QueryTypes/QueryParameter';
